import { useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';
import { formatBytes, formatDate } from '../ui/format';
import { downloadUrl, type FileView } from './api';

/**
 * One file, as a card (Phase 145).
 *
 * The preview is the point of the redesign: a grid of filenames is a grid nobody can scan, and
 * "avatar-01a0d081-0389-77fb…" is not a name a person chose. An image shows itself.
 *
 * **A signed URL is minted only when the card is actually on screen.** It is a credential with a
 * clock on it, and a grid that asked for sixty on load would be handing out sixty nobody looked
 * at. The observer is the whole reason this is a component rather than markup in the page.
 */

export function FileCard({
  file,
  folderName,
  selected,
  onSelect,
  onOpen,
  onToggleFavourite,
  menu,
}: {
  file: FileView;
  folderName: string | null;
  selected: boolean;
  onSelect: (next: boolean) => void;
  onOpen: () => void;
  onToggleFavourite: () => void;
  menu: React.ReactNode;
}): React.JSX.Element {
  const card = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const kind = kindOf(file.mimeType);

  useEffect(() => {
    if (kind !== 'image' || url || failed) return;

    const element = card.current;

    if (!element) return;

    // No IntersectionObserver (jsdom, an old browser): ask straight away rather than never.
    if (typeof IntersectionObserver === 'undefined') {
      downloadUrl(file.id)
        .then((ticket) => setUrl(ticket.url))
        .catch(() => setFailed(true));

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();

        downloadUrl(file.id)
          .then((ticket) => setUrl(ticket.url))
          .catch(() => setFailed(true));
      },
      { rootMargin: '200px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [file.id, kind, url, failed]);

  const extension = (file.name.split('.').pop() ?? '').toUpperCase().slice(0, 4);

  return (
    <div ref={card} className={`file-card${selected ? ' is-selected' : ''}`}>
      <div className="file-card__top">
        <label className="file-card__check">
          <input
            type="checkbox"
            checked={selected}
            aria-label={t('files.select').replace('{name}', file.name)}
            onChange={(event) => onSelect(event.target.checked)}
          />
        </label>

        <div className="file-card__tools">
          <button
            type="button"
            className={`file-card__star${file.favourite ? ' is-on' : ''}`}
            aria-pressed={file.favourite ?? false}
            aria-label={t(file.favourite ? 'files.unfavourite' : 'files.favourite')}
            onClick={onToggleFavourite}
          >
            ★
          </button>

          {menu}
        </div>
      </div>

      <button type="button" className="file-card__preview" onClick={onOpen}>
        {kind === 'image' && url && !failed ? (
          <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />
        ) : (
          <span className={`file-card__glyph file-card__glyph--${kind}`} aria-hidden="true">
            {glyphFor(kind)}
          </span>
        )}

        {extension && <span className="file-card__ext">{extension}</span>}
      </button>

      <button type="button" className="file-card__name" onClick={onOpen} title={file.name}>
        {file.name}
      </button>

      <span className="file-card__where muted small">
        <FolderGlyph />
        {folderName ?? t('files.loose')}
      </span>

      <span className="muted small">
        {formatBytes(file.sizeBytes)} · {formatDate(file.createdAt)}
      </span>
    </div>
  );
}

export function kindOf(mimeType: string): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';

  return 'file';
}

/**
 * What stands in for a file that cannot show itself.
 *
 * A glyph rather than a drawn waveform for audio: a waveform nobody computed is a picture of
 * nothing, and it would claim the product had looked inside the recording.
 */
function glyphFor(kind: ReturnType<typeof kindOf>): string {
  if (kind === 'audio') return '♪';
  if (kind === 'video') return '▶';
  if (kind === 'pdf') return 'PDF';

  return '▤';
}

function FolderGlyph(): React.JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}
